import React, { useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './HeroBanner.css';

const HeroBanner = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slides,       setSlides]       = useState([]);
    const [loading,      setLoading]      = useState(true);

    useEffect(() => {
        const ref = doc(db, 'settings', 'homepage');

        const unsubscribe = onSnapshot(
            ref,
            (snap) => {
                setLoading(false);
                if (!snap.exists()) { setSlides([]); return; }
                const data    = snap.data() || {};
                const banners = (data.promotionalBanners || []).filter(b => b?.enabled);
                const mapped  = banners
                    .map((b, index) => ({ image: b.imageUrl, alt: `Promotional Banner ${index + 1}` }))
                    .filter(b => b.image);
                setSlides(mapped);
            },
            async () => {
                // onSnapshot error — try one-time fetch then stop loading
                try {
                    const snap = await getDoc(ref);
                    if (snap.exists()) {
                        const data    = snap.data() || {};
                        const banners = (data.promotionalBanners || []).filter(b => b?.enabled);
                        const mapped  = banners
                            .map((b, index) => ({ image: b.imageUrl, alt: `Promotional Banner ${index + 1}` }))
                            .filter(b => b.image);
                        setSlides(mapped);
                    }
                } catch (err) {
                    console.error('Failed to load promotional banners:', err);
                } finally {
                    setLoading(false);
                }
            }
        );

        return () => unsubscribe();
    }, []);

    useEffect(() => { setCurrentSlide(0); }, [slides.length]);

    useEffect(() => {
        if (slides.length > 1) {
            const interval = setInterval(() => {
                setCurrentSlide(prev => (prev + 1) % slides.length);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [slides.length]);

    const goToPrevSlide = () => setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
    const goToNextSlide = () => setCurrentSlide(prev => (prev + 1) % slides.length);
    const goToSlide     = (index) => setCurrentSlide(index);

    /* ── Skeleton while loading ── */
    if (loading) {
        return (
            <section className="hero-banner">
                <div className="hero-slider hero-slider--skeleton" />
            </section>
        );
    }

    /* ── No banners configured or all disabled — render nothing ── */
    if (slides.length === 0) return null;

    return (
        <section className="hero-banner">
            <div className="hero-slider">
                <div className="slider-wrapper">
                    {slides.map((slide, index) => (
                        <div
                            key={index}
                            className={`slide ${index === currentSlide ? 'active' : ''}`}
                        >
                            <img src={slide.image} alt={slide.alt} />
                        </div>
                    ))}
                </div>

                {slides.length > 1 && (
                    <>
                        <button
                            className="slider-btn prev-btn"
                            onClick={goToPrevSlide}
                            aria-label="Previous slide"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                        <button
                            className="slider-btn next-btn"
                            onClick={goToNextSlide}
                            aria-label="Next slide"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>

                        <div className="slider-dots">
                            {slides.map((_, index) => (
                                <button
                                    key={index}
                                    className={`dot ${index === currentSlide ? 'active' : ''}`}
                                    onClick={() => goToSlide(index)}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default HeroBanner;